import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Home, Mail, Lock, Eye, EyeOff } from 'lucide-react';

export const LoginPage = () => {
  const { t, language } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success(language === 'fr' ? 'Connexion réussie' : 'تم تسجيل الدخول بنجاح');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Email ou mot de passe incorrect' : 'البريد الإلكتروني أو كلمة المرور غير صحيحة'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-blue-900 rounded-lg flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-manrope font-bold text-xl text-blue-900 leading-none">Beytam</span>
              <span className="text-xs text-slate-400 font-arabic">بيتم</span>
            </div>
          </Link>

          <div className="mb-8">
            <h1 className="font-manrope text-2xl font-bold text-slate-900 mb-2">{t('auth_login')}</h1>
            <p className="text-slate-600">
              {language === 'fr' ? 'Accédez à votre espace personnel' : 'ادخل إلى مساحتك الشخصية'}
            </p>
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth_email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="exemple@email.com"
                      className="pl-10 h-12 rounded-lg"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth_password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 rounded-lg"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <Link to="/forgot-password" className="text-sm text-blue-900 hover:underline">
                    {t('auth_forgot_password')}
                  </Link>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 rounded-lg" 
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? t('common_loading') : t('auth_login')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-slate-600">
            {t('auth_no_account')}{' '}
            <Link to="/register" className="text-blue-900 font-semibold hover:underline">
              {t('auth_create_account')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/11720558/pexels-photo-11720558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
          alt="Real Estate"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-blue-900/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="font-manrope text-3xl font-bold mb-4">
              {language === 'fr' ? 'Bienvenue sur Beytam' : 'مرحباً بك في بيتم'}
            </h2>
            <p className="text-white/80 text-lg">
              {language === 'fr' ? 'La plateforme immobilière de confiance en Algérie' : 'منصة العقارات الموثوقة في الجزائر'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
